import { useMutation, useQueryClient } from '@tanstack/react-query'

import { inviteKeys } from './query-key-factory'

import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'

type CancelInviteResponse = {
  success: boolean
}

export const cancelInvite = async (inviteCode: string): Promise<CancelInviteResponse> => {
  const { error } = await supabase.rpc('cancel_invitation', {
    p_invite_code: inviteCode,
  })

  if (error) {
    logger.error('Failed to cancel invitation', { errorMessage: error, inviteCode })
    throw error
  }

  return {
    success: true,
  }
}

type UseCancelInviteOptions = {
  mutationConfig?: MutationConfig<typeof cancelInvite>
}

export const useCancelInvite = ({ mutationConfig }: UseCancelInviteOptions = {}) => {
  const queryClient = useQueryClient()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      // Invalidate user invitations to refresh the list
      queryClient.invalidateQueries({
        queryKey: inviteKeys.userInvitations(),
      })
      // Also invalidate pending invitations
      queryClient.invalidateQueries({
        queryKey: inviteKeys.userPendingInvitations(),
      })
      // Invalidate the specific invite detail if it was cached
      queryClient.invalidateQueries({
        queryKey: inviteKeys.details(),
      })
      onSuccess?.(...args)
    },
    ...restConfig,
    mutationFn: cancelInvite,
  })
}
